<template>
  <div id="NosotrosComponent" class="about-page">
    <NavBar />

    <main>
      <section class="about-hero" aria-labelledby="about-title">
        <b-container>
          <b-row class="hero-row align-items-center">
            <b-col lg="7" class="hero-copy">
              <span class="eyebrow">CHILL BYTE SOLUTIONS</span>
              <h1 id="about-title">Especialistas que entienden NetSuite y la operación detrás del ERP</h1>
              <p class="hero-lead">
                Ayudamos a empresas que ya utilizan Oracle NetSuite a resolver retos operativos,
                financieros y fiscales con consultoría experta y herramientas conectadas a su ERP.
              </p>
              <div class="hero-actions">
                <a :href="whatsappUrl" class="button button-primary" target="_blank" rel="noopener">
                  Conocer al equipo <b-icon icon="arrow-up-right" />
                </a>
                <a href="#como-trabajamos" class="button button-secondary">
                  Cómo trabajamos <b-icon icon="arrow-down" />
                </a>
              </div>
            </b-col>

            <b-col lg="5" class="hero-people-column">
              <div class="people-visual" aria-label="Parte del equipo de Chill Byte Solutions">
                <span class="visual-label">SOCIAS FUNDADORAS</span>
                <figure class="portrait portrait-primary">
                  <img src="../assets/img/homepage/Foto Magdiel.webp" alt="Integrante del equipo Chill Byte Solutions">
                </figure>
                <figure class="portrait portrait-secondary">
                  <img src="../assets/img/homepage/Foto Guadalupe.webp" alt="Integrante del equipo Chill Byte Solutions">
                </figure>
                <div class="team-note"><i></i><span>Equipo especializado<br><strong>Oracle NetSuite</strong></span></div>
              </div>
            </b-col>
          </b-row>
        </b-container>
      </section>

      <section class="trust-strip" aria-label="Indicadores de experiencia">
        <b-container>
          <div class="trust-grid">
            <div><strong>10+</strong><span>Años de experiencia</span></div>
            <div><strong>98%</strong><span>Satisfacción del cliente</span></div>
            <div><b-icon icon="patch-check" /><span>Profesionales certificados<br>en Oracle NetSuite</span></div>
            <div><b-icon icon="geo-alt" /><span>Experiencia fiscal<br>para México</span></div>
          </div>
        </b-container>
      </section>

      <section class="page-section identity-section">
        <b-container>
          <b-row class="align-items-start">
            <b-col lg="5" class="identity-heading">
              <span class="section-kicker">QUIÉNES SOMOS</span>
              <h2>Tecnología con contexto de negocio</h2>
            </b-col>
            <b-col lg="7" class="identity-copy">
              <p class="lead-copy">
                Somos una consultora tecnológica mexicana especializada en el ecosistema Oracle NetSuite.
                Trabajamos donde el ERP estándar necesita adaptarse a procesos, controles y obligaciones reales.
              </p>
              <p>
                Combinamos conocimiento funcional, desarrollo SuiteCloud e integración de sistemas para construir
                soluciones mantenibles. Esto incluye consultoría y automatización, POS conectado a NetSuite,
                conciliación bancaria y herramientas fiscales para México.
              </p>
              <p>
                No partimos de una herramienta predeterminada. Primero entendemos el flujo, sus excepciones y el
                resultado que necesita el equipo; después definimos la solución adecuada dentro del ecosistema.
              </p>
            </b-col>
          </b-row>
        </b-container>
      </section>

      <section class="page-section expertise-section" aria-labelledby="expertise-title">
        <b-container>
          <header class="split-heading">
            <div><span class="section-kicker light">NUESTRA ESPECIALIDAD</span><h2 id="expertise-title">Un equipo para evolucionar tu operación en NetSuite</h2></div>
            <p>Conectamos la perspectiva funcional, técnica y fiscal que requieren los proyectos empresariales.</p>
          </header>

          <div class="expertise-grid">
            <article v-for="(item, index) in expertise" :key="item.title">
              <span>0{{ index + 1 }}</span>
              <b-icon :icon="item.icon" />
              <h3>{{ item.title }}</h3>
              <p>{{ item.text }}</p>
            </article>
          </div>
        </b-container>
      </section>

      <section class="page-section evidence-section">
        <b-container>
          <header class="split-heading dark-text">
            <div><span class="section-kicker">RESULTADOS EN OPERACIÓN</span><h2>La confianza se demuestra con impacto</h2></div>
            <p>Medimos el valor de una solución por el trabajo que elimina, el control que aporta y la claridad que deja al equipo.</p>
          </header>

          <div class="evidence-grid">
            <article>
              <strong>60<small>%</small></strong>
              <div><h3>menos trabajo manual</h3><p>para un área financiera mediante un Validador XML y Portal de Proveedores.</p></div>
            </article>
            <article>
              <strong>40<small>%</small></strong>
              <div><h3>menos trabajo de trazabilidad</h3><p>de pedimentos asociados a facturas, reforzando el cumplimiento fiscal.</p></div>
            </article>
          </div>
          <small class="evidence-note">Resultados obtenidos en proyectos de clientes. El impacto puede variar según cada operación.</small>
        </b-container>
      </section>

      <section id="como-trabajamos" class="page-section approach-section">
        <b-container>
          <header class="center-heading">
            <span class="section-kicker">CÓMO TRABAJAMOS</span>
            <!-- apply gradient to the second part of the heading -->
            <h2>Cercanía para entender. <span style="background: linear-gradient(90deg, #073b8c, #4f46e5); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">Rigor para ejecutar.</span></h2>
            <p>Una relación clara desde el diagnóstico hasta la evolución de la solución.</p>
          </header>

          <div class="approach-grid">
            <article v-for="(item, index) in approach" :key="item.title">
              <span>0{{ index + 1 }}</span>
              <h3>{{ item.title }}</h3>
              <p>{{ item.text }}</p>
            </article>
          </div>
        </b-container>
      </section>

      <section class="page-section purpose-section">
        <b-container>
          <div class="purpose-grid">
            <article class="purpose-lead">
              <span class="section-kicker light">NUESTRO PROPÓSITO</span>
              <h2>Construir soluciones que sigan siendo útiles después del go-live</h2>
            </article>
            <article>
              <span>01 / MISIÓN</span>
              <h3>Crear valor operativo real</h3>
              <p>Acompañar a nuestros clientes con soluciones confiables, ágiles y centradas en negocio que conviertan retos operativos en eficiencia medible.</p>
            </article>
            <article>
              <span>02 / VISIÓN</span>
              <h3>Ser un socio tecnológico de largo plazo</h3>
              <p>Ser reconocidos por nuestra especialización, claridad y capacidad para evolucionar junto con la operación de nuestros clientes.</p>
            </article>
          </div>
        </b-container>
      </section>

      <section class="page-section values-section">
        <b-container>
          <header class="split-heading dark-text">
            <div><span class="section-kicker">PRINCIPIOS DE TRABAJO</span><h2>Lo que puedes <span style="background: linear-gradient(90deg, #073b8c, #4f46e5); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">esperar de nosotros</span></h2></div>
            <p>Principios concretos que orientan decisiones, comunicación y entregables.</p>
          </header>
          <div class="values-list">
            <article v-for="(value, index) in values" :key="value.title">
              <span>0{{ index + 1 }}</span><b-icon :icon="value.icon" /><h3>{{ value.title }}</h3><p>{{ value.text }}</p>
            </article>
          </div>
        </b-container>
      </section>

      <section class="page-section faq-section" aria-labelledby="faq-title">
        <b-container><b-row>
          <b-col lg="4" class="faq-heading"><span class="section-kicker">PREGUNTAS FRECUENTES</span><h2 id="faq-title">Antes de trabajar juntos</h2><p>Respuestas directas sobre nuestro enfoque y experiencia.</p></b-col>
          <b-col lg="8">
            <details v-for="(item, index) in faqs" :key="item.question" :open="index === 0">
              <summary>{{ item.question }} <b-icon icon="plus" /></summary><p>{{ item.answer }}</p>
            </details>
          </b-col>
        </b-row></b-container>
      </section>

      <section class="conversion-section">
        <b-container><div class="conversion-content">
          <div><span>HABLEMOS DE TU OPERACIÓN</span><h2>Un buen proyecto comienza con las preguntas correctas</h2><p>Cuéntanos qué proceso necesitas mejorar en Oracle NetSuite.</p></div>
          <a :href="whatsappUrl" target="_blank" rel="noopener">Hablar con un especialista <b-icon icon="whatsapp" /></a>
        </div></b-container>
      </section>

      <section class="page-section contact-section">
        <b-container>
          <header class="center-heading"><span class="section-kicker">CONTACTO</span><h2>Conversemos sobre tu siguiente proyecto</h2></header>
          <ContactForm />
        </b-container>
      </section>
    </main>

    <FooterChillByte />
  </div>
</template>

<script>
import NavBar from "../components/Template/NavBar.vue";
import Footer from "../components/Template/Footer.vue";
import ContactForm from "@/components/Template/ContactForm.vue";

const SITE_URL = "https://www.chillbytesolutions.com";

export default {
  name: "NosotrosView",
  components: { NavBar, FooterChillByte: Footer, ContactForm },
  data() {
    return {
      whatsappUrl: "https://api.whatsapp.com/send?phone=4423336440&text=Hola%2C%20quiero%20conocer%20m%C3%A1s%20sobre%20el%20equipo%20y%20los%20servicios%20de%20Chill%20Byte",
      expertise: [
        { icon: "gear-wide-connected", title: "Consultoría NetSuite", text: "Análisis funcional, optimización post go-live y evolución del ERP según la operación." },
        { icon: "code-square", title: "Desarrollo SuiteCloud", text: "SuiteScript, workflows, reportes y herramientas compatibles con NetSuite." },
        { icon: "diagram-3", title: "Integraciones", text: "Conexión segura entre NetSuite, sistemas externos y herramientas operativas." },
        { icon: "file-earmark-check", title: "Cumplimiento México", text: "Soluciones CFDI, XML, proveedores, Carta Porte, pedimentos y contabilidad electrónica." }
      ],
      approach: [
        { title: "Escuchamos el proceso", text: "Entendemos usuarios, excepciones, controles y consecuencias antes de proponer tecnología." },
        { title: "Hacemos visible el alcance", text: "Definimos responsabilidades, entregables y criterios de éxito con claridad." },
        { title: "Validamos antes de liberar", text: "Probamos la solución en un ambiente controlado y con escenarios reales." },
        { title: "Acompañamos la evolución", text: "Damos continuidad al resultado y ajustamos la solución cuando la operación cambia." }
      ],
      values: [
        { icon: "check2-circle", title: "Compromiso", text: "Asumimos los objetivos del proyecto con responsabilidad y seguimiento." },
        { icon: "chat-square-text", title: "Claridad", text: "Comunicamos decisiones, riesgos y avances sin lenguaje innecesariamente complejo." },
        { icon: "shield-check", title: "Calidad", text: "Construimos pensando en seguridad, mantenimiento y continuidad operativa." },
        { icon: "people", title: "Colaboración", text: "Trabajamos con los equipos que conocen el proceso, no alrededor de ellos." }
      ],
      faqs: [
        { question: "¿Chill Byte Solutions está especializado en Oracle NetSuite?", answer: "Sí. Nuestro equipo incluye profesionales certificados en Oracle NetSuite y trabajamos con empresas que necesitan optimizar, extender o integrar una cuenta activa del ERP." },
        { question: "¿Trabajan únicamente con empresas de México?", answer: "Nuestra experiencia principal está en México y en sus requerimientos fiscales. Podemos evaluar proyectos de otras ubicaciones según el alcance funcional y técnico." },
        { question: "¿Desarrollan productos propios además de consultoría?", answer: "Sí. Contamos con herramientas compatibles con NetSuite, como POS, Conciliador Bancario y soluciones fiscales que incluyen Validador XML y Portal de Proveedores." },
        { question: "¿Cómo comienza un proyecto con ustedes?", answer: "Comenzamos con una conversación de diagnóstico para entender el proceso, el problema y el resultado esperado. Con esa información definimos el siguiente paso y el alcance adecuado." }
      ]
    };
  },
  metaInfo() {
    const title = "Nosotros | Consultores Oracle NetSuite en México | Chill Byte";
    const description = "Conoce a Chill Byte Solutions, consultora mexicana especializada en Oracle NetSuite, SuiteCloud, integraciones y soluciones fiscales para empresas.";
    const canonicalUrl = `${SITE_URL}/nosotros`;
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Chill Byte Solutions",
      url: SITE_URL,
      description,
      areaServed: { "@type": "Country", name: "México" },
      knowsAbout: ["Oracle NetSuite", "SuiteCloud", "SuiteScript", "Integraciones ERP", "CFDI 4.0", "Cumplimiento fiscal en México"]
    };
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: this.faqs.map(item => ({ "@type": "Question", name: item.question, acceptedAnswer: { "@type": "Answer", text: item.answer } }))
    };

    return {
      title,
      link: [{ rel: "canonical", href: canonicalUrl }],
      meta: [
        { vmid: "description", name: "description", content: description },
        { vmid: "robots", name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" },
        { vmid: "og:type", property: "og:type", content: "website" },
        { vmid: "og:locale", property: "og:locale", content: "es_MX" },
        { vmid: "og:title", property: "og:title", content: title },
        { vmid: "og:description", property: "og:description", content: description },
        { vmid: "og:url", property: "og:url", content: canonicalUrl },
        { vmid: "twitter:card", name: "twitter:card", content: "summary_large_image" },
        { vmid: "twitter:title", name: "twitter:title", content: title },
        { vmid: "twitter:description", name: "twitter:description", content: description }
      ],
      script: [
        { vmid: "about-organization", type: "application/ld+json", innerHTML: JSON.stringify(organizationSchema) },
        { vmid: "about-faq", type: "application/ld+json", innerHTML: JSON.stringify(faqSchema) }
      ],
      __dangerouslyDisableSanitizersByTagID: { "about-organization": ["innerHTML"], "about-faq": ["innerHTML"] }
    };
  }
};
</script>

<style scoped lang="scss">
$ink: #101828;
$muted: #475467;
$line: #dbe3ee;
$blue: #146ef5;
$deep: #073b8c;
$surface: #f4f7fb;
$navy: #0c1b33;

.about-page { color: $ink; background: #fff; letter-spacing: 0; }
.about-hero { position: relative; overflow: hidden; background: #f7f9fc; border-bottom: 1px solid $line; }
.about-hero::before { content: ""; position: absolute; inset: 0; background-image: linear-gradient(rgba(20,110,245,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(20,110,245,.05) 1px,transparent 1px); background-size: 48px 48px; mask-image: linear-gradient(to right,#000,transparent 78%); }
.hero-row { position: relative; min-height: 670px; padding: 70px 0; }
.hero-copy { padding-right: 55px; }
.eyebrow,.section-kicker { display: block; color: $deep; font-size: .75rem; font-weight: 800; letter-spacing: .12em; margin-bottom: 20px; }
.section-kicker.light { color: #68d8ec; }
.hero-copy h1 { color: $ink !important; font-size: clamp(3rem,5vw,4.8rem) !important; font-weight: 800; line-height: 1; margin: 0 0 26px; }
.hero-lead { max-width: 700px; color: $muted; font-size: 1.18rem; line-height: 1.7; }
.hero-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 32px; }
.button { min-height: 52px; display: inline-flex; align-items: center; justify-content: center; gap: 10px; padding: 13px 22px; border-radius: 6px; font-weight: 750; text-decoration: none; }
.button-primary { color: #fff; background: $blue; box-shadow: 0 12px 28px rgba(20,110,245,.22); }
.button-primary:hover { color: #fff; background: #075fdc; text-decoration: none; }
.button-secondary { color: $ink; background: #fff; border: 1px solid #cbd5e1; }
.button-secondary:hover { color: $blue; text-decoration: none; }
.hero-people-column { display: flex; justify-content: center; }
.people-visual { position: relative; width: 430px; height: 470px; }
.visual-label { position: absolute; top: 12px; left: 50%; transform: translateX(-50%); color: #667085; font-size: .67rem; font-weight: 800; letter-spacing: .13em; white-space: nowrap; }
.portrait { position: absolute; margin: 0; overflow: hidden; background: #eef2f6; border: 1px solid $line; border-radius: 50%; box-shadow: 0 20px 60px rgba(16,24,40,.12); }
.portrait img { width: 100%; height: 100%; object-fit: contain; }
.portrait-primary { width: 280px; height: 280px; left: 18px; top: 68px; }
.portrait-secondary { width: 190px; height: 190px; right: 0; bottom: 35px; border: 8px solid #f7f9fc; }
.team-note { position: absolute; display: flex; align-items: center; gap: 11px; right: 8px; top: 98px; padding: 12px 15px; background: #fff; border: 1px solid $line; border-radius: 6px; box-shadow: 0 12px 28px rgba(16,24,40,.1); color: $muted; font-size: .72rem; line-height: 1.35; }
.team-note i { width: 8px; height: 8px; border-radius: 50%; background: #12a679; box-shadow: 0 0 0 5px rgba(18,166,121,.12); }
.team-note strong { color: $ink; }
.trust-strip { background: #fff; border-bottom: 1px solid $line; }
.trust-grid { display: grid; grid-template-columns: repeat(4,1fr); }
.trust-grid > div { min-height: 105px; display: flex; align-items: center; justify-content: center; gap: 14px; padding: 22px; border-right: 1px solid $line; }
.trust-grid > div:first-child { border-left: 1px solid $line; }
.trust-grid strong { color: $ink; font-size: 2rem; line-height: 1; }
.trust-grid svg { width: 27px; height: 27px; color: $blue; }
.trust-grid span { color: $muted; font-size: .78rem; line-height: 1.4; }
.page-section { padding: 105px 0; }
.identity-section { background: #fff; }
.identity-heading h2,.split-heading h2,.center-heading h2,.faq-heading h2 { color: $ink; font-size: clamp(2.15rem,4vw,3.25rem); font-weight: 800; line-height: 1.08; margin: 0; }
.identity-copy { padding-left: 50px; }
.identity-copy p { color: $muted; font-size: 1rem; line-height: 1.78; }
.identity-copy .lead-copy { color: $ink; font-size: 1.25rem; line-height: 1.65; }
.expertise-section { color: #fff; background: $navy; }
.split-heading { display: grid; grid-template-columns: 1.2fr .8fr; align-items: end; gap: 70px; margin-bottom: 52px; }
.expertise-section .split-heading h2 { color: #fff; }
.split-heading > p { color: #b9c5d8; font-size: 1.04rem; line-height: 1.7; margin: 0; }
.split-heading.dark-text > p { color: $muted; }
.expertise-grid { display: grid; grid-template-columns: repeat(4,1fr); border-top: 1px solid rgba(255,255,255,.15); border-bottom: 1px solid rgba(255,255,255,.15); }
.expertise-grid article { position: relative; min-height: 295px; padding: 30px 26px; border-right: 1px solid rgba(255,255,255,.15); }
.expertise-grid article:last-child { border-right: 0; }
.expertise-grid article > span { position: absolute; top: 18px; right: 18px; color: #6c7d95; font-family: "IBM Plex Mono",monospace; font-size: .72rem; }
.expertise-grid svg { width: 29px; height: 29px; color: #67d6ea; margin: 10px 0 45px; }
.expertise-grid h3 { color: #fff; font-size: 1.12rem; font-weight: 800; }
.expertise-grid p { color: #b9c5d8; font-size: .92rem; line-height: 1.65; }
.evidence-section { background: $surface; }
.evidence-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 18px; }
.evidence-grid article { min-height: 230px; display: grid; grid-template-columns: .4fr .6fr; align-items: center; gap: 25px; padding: 30px; background: #fff; border: 1px solid $line; border-radius: 8px; }
.evidence-grid strong { color: $blue; font-size: 4.5rem; line-height: 1; }
.evidence-grid strong small { font-size: 2rem; }
.evidence-grid h3 { color: $ink; font-size: 1.15rem; font-weight: 800; }
.evidence-grid p { color: $muted; line-height: 1.6; margin: 0; }
.evidence-note { display: block; color: #667085; margin-top: 22px; text-align: right; }
.approach-section { background: #fff; }
.center-heading { max-width: 760px; margin: 0 auto 50px; text-align: center; }
.center-heading > p { color: $muted; font-size: 1.04rem; margin-top: 18px; }
.approach-grid { display: grid; grid-template-columns: repeat(4,1fr); border-top: 1px solid $line; border-bottom: 1px solid $line; }
.approach-grid article { position: relative; min-height: 240px; padding: 32px 26px; border-right: 1px solid $line; }
.approach-grid article:last-child { border-right: 0; }
.approach-grid article > span { display: block; color: $blue; font-family: "IBM Plex Mono",monospace; font-size: .72rem; margin-bottom: 48px; }
.approach-grid h3 { color: $ink; font-size: 1.12rem; font-weight: 800; }
.approach-grid p { color: $muted; font-size: .92rem; line-height: 1.65; }
.purpose-section { color: #fff; background: $blue; }
.purpose-grid { display: grid; grid-template-columns: 1.2fr .9fr .9fr; gap: 0; }
.purpose-grid article { min-height: 310px; padding: 35px; border-left: 1px solid rgba(255,255,255,.22); }
.purpose-grid article:last-child { border-right: 1px solid rgba(255,255,255,.22); }
.purpose-grid h2 { color: #fff; font-size: clamp(2rem,3.5vw,3rem); font-weight: 800; line-height: 1.1; }
.purpose-grid article > span { display: block; color: rgba(255,255,255,.75); font-size: .7rem; font-weight: 800; letter-spacing: .1em; margin-bottom: 55px; }
.purpose-grid h3 { color: #fff; font-size: 1.2rem; font-weight: 800; }
.purpose-grid p { color: rgba(255,255,255,.82); line-height: 1.7; }
.values-section { background: #fff; }
.values-list { border-top: 1px solid $line; }
.values-list article { display: grid; grid-template-columns: 55px 45px .45fr .55fr; align-items: center; gap: 20px; min-height: 110px; border-bottom: 1px solid $line; }
.values-list article > span { color: #98a2b3; font-family: "IBM Plex Mono",monospace; font-size: .72rem; }
.values-list svg { color: $blue; width: 24px; height: 24px; }
.values-list h3 { color: $ink; font-size: 1.08rem; font-weight: 800; margin: 0; }
.values-list p { color: $muted; line-height: 1.6; margin: 0; }
.faq-section { background: $surface; }
.faq-heading { padding-right: 45px; }
.faq-heading p { color: $muted; line-height: 1.7; margin-top: 18px; }
.faq-section details { border-top: 1px solid #cbd5e1; }
.faq-section details:last-child { border-bottom: 1px solid #cbd5e1; }
.faq-section summary { display: flex; justify-content: space-between; align-items: center; gap: 20px; padding: 23px 4px; color: $ink; font-weight: 800; cursor: pointer; list-style: none; }
.faq-section details p { color: $muted; line-height: 1.7; padding: 0 45px 23px 4px; margin: 0; }
.faq-section details[open] summary svg { transform: rotate(45deg); }
.conversion-section { padding: 70px 0; color: #fff; background: $navy; }
.conversion-content { display: flex; align-items: center; justify-content: space-between; gap: 40px; }
.conversion-content > div { max-width: 760px; }
.conversion-content span { color: #68d8ec; font-size: .72rem; font-weight: 800; letter-spacing: .12em; }
.conversion-content h2 { color: #fff; font-size: clamp(2rem,3.5vw,3rem); font-weight: 800; margin: 10px 0; }
.conversion-content p { color: #b9c5d8; margin: 0; }
.conversion-content a { flex: 0 0 auto; min-height: 52px; display: inline-flex; align-items: center; justify-content: center; gap: 10px; padding: 13px 22px; color: $deep; background: #fff; border-radius: 6px; font-weight: 800; }
.conversion-content a:hover { color: $blue; text-decoration: none; }
.contact-section { background: #fff; }

@media (max-width: 991px) {
  .hero-row { min-height: auto; }
  .hero-copy { padding-right: 15px; }
  .hero-people-column { margin-top: 48px; }
  .trust-grid { grid-template-columns: repeat(2,1fr); }
  .identity-copy { padding-left: 15px; margin-top: 28px; }
  .split-heading { grid-template-columns: 1fr; gap: 16px; }
  .expertise-grid,.approach-grid { grid-template-columns: repeat(2,1fr); }
  .expertise-grid article:nth-child(2),.approach-grid article:nth-child(2) { border-right: 0; }
  .expertise-grid article:nth-child(-n+2) { border-bottom: 1px solid rgba(255,255,255,.15); }
  .approach-grid article:nth-child(-n+2) { border-bottom: 1px solid $line; }
  .evidence-grid { grid-template-columns: 1fr; }
  .purpose-grid { grid-template-columns: 1fr; }
  .purpose-grid article { min-height: auto; border-right: 1px solid rgba(255,255,255,.22); border-bottom: 1px solid rgba(255,255,255,.22); }
  .conversion-content { align-items: flex-start; flex-direction: column; }
}

@media (max-width: 575px) {
  .hero-row { padding: 58px 0 35px; }
  .hero-copy h1 { font-size: 2.65rem !important; }
  .hero-lead { font-size: 1.03rem; }
  .hero-actions { flex-direction: column; }
  .hero-actions .button { width: 100%; }
  .people-visual { width: 100%; height: 400px; transform: scale(.9); }
  .portrait-primary { left: 0; }
  .trust-grid { grid-template-columns: 1fr; }
  .trust-grid > div { justify-content: flex-start; border-left: 1px solid $line; border-bottom: 1px solid $line; }
  .page-section { padding: 65px 0; }
  .expertise-grid,.approach-grid { grid-template-columns: 1fr; }
  .expertise-grid article,.approach-grid article { min-height: 220px; border-right: 0; border-bottom: 1px solid $line; }
  .expertise-grid article { border-bottom-color: rgba(255,255,255,.15); }
  .evidence-grid article { grid-template-columns: 1fr; }
  .evidence-note { text-align: left; }
  .values-list article { grid-template-columns: 35px 35px 1fr; padding: 20px 0; }
  .values-list p { grid-column: 3; }
  .conversion-content a { width: 100%; }
}

@media (prefers-reduced-motion: reduce) {
  * { scroll-behavior: auto !important; transition: none !important; animation: none !important; }
}
</style>